import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import SiteCard from "@/components/feature/SiteCard";
import { useSEO } from "@/hooks/useSEO";
import { getCategoryByName, getSitesByCategory } from "@/lib/navData";
import type { SiteWithCategory } from "@/types/nav";

type SortKey = "hot" | "latest";

export default function CategoryPage() {
  const { categoryId } = useParams();
  const decoded = decodeURIComponent(categoryId ?? "");
  const category = getCategoryByName(decoded);

  const [sub, setSub] = useState("全部");
  const [sort, setSort] = useState<SortKey>("hot");
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);

  useSEO(
    `${category ? category.name : "分类"} - 51网址导航`,
    category
      ? `在51网址导航的【${category.name}】分类，快速找到优质${category.name}相关网站入口。`
      : "在51网址导航中按分类浏览优质网站入口。",
    `/category/${encodeURIComponent(decoded)}`,
  );

  const allSites = useMemo(
    () => (category ? getSitesByCategory(category.id) : []),
    [category],
  );

  const filtered = useMemo(() => {
    let list: SiteWithCategory[] = allSites;
    if (sub !== "全部") {
      list = list.filter((s) => s.subCategory === sub);
    }
    return [...list].sort((a, b) =>
      sort === "hot" ? b.visits - a.visits : a.addedAt < b.addedAt ? 1 : -1,
    );
  }, [allSites, sub, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const resetFilters = () => {
    setSub("全部");
    setSort("hot");
    setPageSize(20);
    setPage(1);
  };

  const changeSub = (value: string) => {
    setSub(value);
    setPage(1);
  };

  if (!category) {
    return (
      <div className="max-w-page mx-auto px-4 md:px-6 py-16">
        <EmptyState
          icon="ri-error-warning-line"
          title="未找到该分类"
          description="您访问的分类不存在或已调整，请返回首页重新选择分类。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  const subTabs = ["全部", ...category.subCategories];

  return (
    <div className="max-w-page mx-auto px-4 md:px-6 py-8 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: category.name }]} />

      <div className="mt-5 p-6 md:p-8 rounded-2xl bg-gradient-to-br from-primary-100 to-background-100 border border-primary-200/60">
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-background-50 flex items-center justify-center shrink-0">
            <i className={`${category.icon} text-2xl`} />
          </span>
          <div>
            <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
              {category.name}
            </h1>
            <p className="text-sm text-foreground-500 mt-1">
              共收录 {allSites.length} 个优质网站入口
            </p>
          </div>
        </div>
        <p className="text-sm md:text-base text-foreground-600 mt-4 leading-relaxed">
          {category.description}
        </p>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mt-6">
        <div className="flex flex-wrap gap-2">
          {subTabs.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => changeSub(tab)}
              className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                sub === tab
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-foreground-500">
            排序
            <select
              value={sort}
              onChange={(e) => {
                setSort(e.target.value as SortKey);
                setPage(1);
              }}
              className="px-3 py-2 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 cursor-pointer"
            >
              <option value="hot">按热门程度</option>
              <option value="latest">按最新添加</option>
            </select>
          </label>

          <label className="flex items-center gap-2 text-sm text-foreground-500">
            每页
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="px-3 py-2 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 cursor-pointer"
            >
              <option value={20}>20 条</option>
              <option value={50}>50 条</option>
            </select>
          </label>
        </div>
      </div>

      <div className="mt-6">
        {pageItems.length === 0 ? (
          <EmptyState
            icon="ri-search-eye-line"
            title="暂无相关网站，您可以尝试调整筛选条件。"
            description="当前筛选条件下没有匹配的网站，清除筛选可查看该分类下的全部内容。"
            actionLabel="清除筛选"
            onAction={resetFilters}
          />
        ) : (
          <>
            <div className="flex flex-col gap-3">
              {pageItems.map((site) => (
                <SiteCard key={site.id} site={site} showCategory={false} />
              ))}
            </div>
            <Pagination currentPage={safePage} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </div>

      <div className="mt-8 text-center">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line" />
          返回首页浏览更多分类
        </Link>
      </div>
    </div>
  );
}